import { useQuery } from "@apollo/client/react";
import { Button, Center, Loader, Stack, Text, Title } from "@mantine/core";
import { readFragment } from "gql.tada";
import { useState } from "react";

import {
  JOURNAL_ENTRIES_QUERY,
  JournalEntrySummaryFragment,
} from "./JournalFeed.graphql";
import { JournalEntryCard } from "./JournalEntryCard";
import type { JournalEntryCardEntry } from "./JournalEntryCard";
import { JournalFilterBar } from "./filters/JournalFilterBar";
import {
  useJournalLiveTail,
  type JournalFiltersInput,
} from "./hooks/useJournalLiveTail";

interface Props {
  defaultFilters: JournalFiltersInput;
  title?: string;
  /** When true, hide the filter bar entirely (callers control filters). */
  readOnlyFilters?: boolean;
}

/**
 * Streams journal entries for the given default filters. Callers that need
 * to refresh the filter scope (e.g. when the user navigates between
 * resources in a drill-down) should pass a stable React `key` derived from
 * the scope so the component remounts with fresh state — we intentionally
 * do NOT mirror `defaultFilters` into state on every render because
 * callers commonly recreate the object on every render and that would
 * stomp on user-edited filter selections in the filter bar.
 */
export function JournalFeed({ defaultFilters, title, readOnlyFilters }: Props) {
  const [filters, setFilters] = useState<JournalFiltersInput>(defaultFilters);

  const { data, loading, error, fetchMore } = useQuery(JOURNAL_ENTRIES_QUERY, {
    variables: { filters },
    notifyOnNetworkStatusChange: true,
  });

  useJournalLiveTail(filters);

  const entries: JournalEntryCardEntry[] = (
    data?.journalEntries.edges ?? []
  ).map((edge) => edge.node);

  const pageInfo = data?.journalEntries.pageInfo;

  return (
    <Stack gap="md">
      {title && <Title order={3}>{title}</Title>}
      {!readOnlyFilters && (
        <JournalFilterBar defaultFilters={filters} onApply={setFilters} />
      )}
      {loading && entries.length === 0 && (
        <Center>
          <Loader />
        </Center>
      )}
      {error && <Text c="red">Error: {error.message}</Text>}
      {!loading && !error && entries.length === 0 && (
        <Text c="dimmed">No journal entries match these filters.</Text>
      )}
      <Stack gap="xs">
        {entries.map((entry) => {
          const { id } = readFragment(JournalEntrySummaryFragment, entry);
          return <JournalEntryCard key={id} entry={entry} />;
        })}
      </Stack>
      {pageInfo?.hasNextPage && (
        <Center>
          <Button
            variant="default"
            loading={loading}
            onClick={() =>
              fetchMore({
                variables: {
                  filters,
                  page: { after: pageInfo.endCursor },
                },
                // Apollo's default `fetchMore` REPLACES the cached query
                // result with the new page. For an append-style "Load
                // more" feed we must merge the new edges into the
                // existing list and carry the new pageInfo forward.
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev;
                  return {
                    ...prev,
                    journalEntries: {
                      ...fetchMoreResult.journalEntries,
                      edges: [
                        ...prev.journalEntries.edges,
                        ...fetchMoreResult.journalEntries.edges,
                      ],
                    },
                  };
                },
              })
            }
          >
            Load more
          </Button>
        </Center>
      )}
    </Stack>
  );
}
